/**
 * Which of a client's events a user may reach.
 *
 * `all` and `listed` are separate states, not one derived from the other: an unrestricted user is
 * not "a user who happens to have every current event selected". The difference is what happens to
 * an event created tomorrow — `all` includes it, `listed` does not.
 *
 * On the wire the contract is `{kind:'all'} ⇄ []` and `{kind:'listed'} ⇄ the id array`. That reads
 * backwards at first glance, which is exactly why it needs a named type rather than a bare Set: a
 * `Set` makes "everything" and "nothing" the same value.
 *
 * Defined here rather than in auth-session because event-domain owns `ClientUser` and depends on
 * nothing; auth-session re-exports it so its own consumers are unaffected.
 */
export type EventScope =
  | { readonly kind: 'all' }
  | { readonly kind: 'listed'; readonly eventIds: ReadonlySet<string> };

/**
 * The empty array is the contract's way of saying "unrestricted", not "nothing".
 *
 * Takes `unknown` because it reads network JSON. The generated DTO declares `string[] | undefined`,
 * but a declared type is not a runtime guarantee: a bare `"evt-1"` would otherwise be spread into a
 * five-element set of characters, and a `null` in the array would be written straight back to the
 * API. Emptiness is judged on the raw value, before filtering, so a non-empty scope whose ids are
 * unreadable stays a restriction rather than collapsing into unrestricted access.
 */
export function toEventScope(value: unknown): EventScope {
  if (!Array.isArray(value) || value.length === 0) {
    return { kind: 'all' };
  }

  return {
    kind: 'listed',
    eventIds: new Set(
      value.filter((id): id is string => typeof id === 'string')
    ),
  };
}

/** Back to the wire. `all` serialises as the empty array, which is what the API expects. */
export function fromEventScope(scope: EventScope): ReadonlyArray<string> {
  return scope.kind === 'all' ? [] : [...scope.eventIds];
}

/**
 * Builds a restriction, collapsing an empty one to `all`.
 *
 * `{kind:'listed', eventIds: ∅}` is representable in memory but meaningless on the wire: it
 * serialises to `[]`, which reads back as unrestricted. Left constructible, the two functions
 * disagree — `isInEventScope` denies everything for it while a save grants everything — so the
 * state is normalised away at the only place it can be built.
 */
export function listedScope(eventIds: Iterable<string>): EventScope {
  const ids = new Set(eventIds);
  return ids.size === 0 ? { kind: 'all' } : { kind: 'listed', eventIds: ids };
}

export function isInEventScope(scope: EventScope, eventId: string): boolean {
  return scope.kind === 'all' || scope.eventIds.has(eventId);
}
